import {
    useGetList,
    useTranslate,
    useRecordContext,
    useGetMany,
} from 'react-admin';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import {
    Box,
    Checkbox,
    FormControlLabel,
    Typography,
    IconButton,
    Tooltip,
    TextField,
    Autocomplete,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useEffect, useState } from 'react';

const useDebounce = (value, delay = 500) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
};

const InstanceTemplateArrayInput = ({ source, filter }) => {
    const translate = useTranslate();
    const { control, watch } = useFormContext();
    const { fields, append, remove } = useFieldArray({ control, name: source });
    const [selectedOption, setSelectedOption] = useState(null);
    const [inputValue, setInputValue] = useState('');

    const perPage = process.env.NODE_ENV === 'development' ? 100 : 500;

    //Wait for 500 milliseconds (0.5 seconds) after the user stops typing before updating the debounced value
    const debouncedInputValue = useDebounce(inputValue, 500);

    const { data: templates = [], isLoading } = useGetList('instanceTemplates', {
        filter: debouncedInputValue
            ? { ...filter, name: debouncedInputValue }
            : filter,
        pagination: { page: 1, perPage },
        sort: { field: 'name', order: 'ASC' },
    });

    const selectedIds = watch(source)?.map((f) => f.instanceTemplateId) || [];

    const { data: selectedTemplates = [] } = useGetMany('instanceTemplates', {
        ids: selectedIds,
    }, { enabled: selectedIds.length > 0 });

    const allTemplates = [...templates, ...selectedTemplates].reduce((acc, curr) => {
        if (!acc.find(t => t.id === curr.id)) acc.push(curr);
        return acc;
    }, []);

    const handleAdd = (event, value) => {
        if (value && value.id && !selectedIds.includes(value.id)) {
            append({ instanceTemplateId: value.id, shouldStop: true });
        }
        setSelectedOption(null);
        setInputValue('');
    };

    const options = templates.filter(t => !selectedIds.includes(t.id));

    return (
        <Box sx={{ mt: 2, minWidth: '400px', maxWidth: '700px', width: '60ch' }}>
            <Autocomplete
                value={selectedOption}
                inputValue={inputValue}
                onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                        setInputValue(newInputValue);
                    }
                }}
                onChange={(event, value) => handleAdd(event, value)}
                 options={options}
                 //options={allTemplates}
                filterOptions={(options) => options} 
                getOptionLabel={(option) =>
                    option?.id ? `${option.name} #${option.language} #${option.snapshotVersion}` : ''
                }
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                loading={isLoading}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={translate('app.trainingTemplates.create.chooseInstance')}
                        variant="outlined"
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props}>
                        {`${option.name} #${option.language} #${option.snapshotVersion}`}
                    </li>
                )}
                sx={{ width: '100%' }}
                ListboxProps={{
                    style: {
                        maxWidth: '700px',
                        whiteSpace: 'normal',
                    }
                }}
            />

            {fields.map((field, index) => {
                const template = allTemplates.find(t => t.id === field.instanceTemplateId);
                return (
                    <Box
                        key={field.id}
                        sx={{
                            mt: 1,
                            px: 1,
                            py: 0.5,
                            border: '1px solid #ccc',
                            borderRadius: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '0.875rem',
                            minHeight: '48px',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    flex: 1,
                                    minWidth: 0,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {`${template?.name || field.instanceTemplateId} #${template?.language || ''} #${template?.snapshotVersion ?? ''}`}
                            </Typography>

                            <Controller
                                name={`${source}[${index}].shouldStop`}
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...field}
                                                checked={!!field.value}
                                                size="small"
                                                sx={{ p: 0.5 }}
                                            />
                                        }
                                        label={
                                            <Tooltip title="Instance will be stopped after deployment" arrow>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', cursor: 'help' }}>
                                                    Should be stopped
                                                </Typography>
                                            </Tooltip>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ minWidth: '48px', display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton size="small" onClick={() => remove(index)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default InstanceTemplateArrayInput;
